import React, { createContext, useContext, useRef, useState } from 'react';
import { noop, useQuery } from '@tanstack/react-query';
import {
  standardRequest,
  standardRequestWithErrorFlash,
} from 'shared/utils/request';
import { useUpdateEffect } from 'react-use';
import { useTalentMatchingTabContext } from './talent_matching_tab_context';

type RankingInProgressContextType = {
  rankingInProgress: boolean;
  rankingCompleted: boolean;
  isBackgroundRanking: boolean;
  hasCalibrationSetting: boolean;
  enablePolling: () => void;
  dismissRankingCompleted: () => void;
};

const RankingInProgressContext = createContext<RankingInProgressContextType>({
  rankingInProgress: false,
  rankingCompleted: false,
  isBackgroundRanking: false,
  hasCalibrationSetting: false,
  enablePolling: noop,
  dismissRankingCompleted: noop,
});

export type RankingTrigger = 'background' | 'calibration';

export const RankingInProgressProvider = ({
  rankingTrigger,
  previouslyCalibrated,
  interval = 2000,
  children,
}: {
  rankingTrigger: RankingTrigger | null;
  previouslyCalibrated: boolean;
  interval?: number;
  children: React.ReactNode;
}) => {
  const { paths } = useTalentMatchingTabContext();
  const pollOnLoad = rankingTrigger !== null;
  const [pollingEnabled, setPollingEnabled] = useState(pollOnLoad);
  const [hasCalibrationSetting, setHasCalibrationSetting] =
    useState(previouslyCalibrated);

  // This keeps the UI from showing the progress component until
  // after the first polling request. That way we can check if
  // ranking is in progress without flashing a loading state if
  // no ranking is happening.
  const [firstPoll, setFirstPoll] = useState(!pollOnLoad);
  const [rankingCompleted, setRankingCompleted] = useState(false);
  const [isBackgroundRanking, setIsBackgroundRanking] = useState(
    rankingTrigger === 'background'
  );
  const isBackgroundRankingRef = useRef(rankingTrigger === 'background');
  const wasRankingInProgress = useRef(pollOnLoad);
  const errorShown = useRef(false);

  useUpdateEffect(() => {
    if (pollingEnabled) {
      setFirstPoll(true);
      setHasCalibrationSetting(previouslyCalibrated);
      setRankingCompleted(false);
      wasRankingInProgress.current = false;
      errorShown.current = false;
    }
  }, [pollingEnabled]);

  const query = useQuery({
    queryKey: ['rankingInProgress'],
    queryFn: async () => {
      const standardRequestWithOrWithoutError = errorShown.current
        ? standardRequest
        : standardRequestWithErrorFlash;

      try {
        const { data } = await standardRequestWithOrWithoutError.get(
          paths.rankingInProgressPath
        );

        errorShown.current = data.status === 'error';

        setFirstPoll(false);

        if (data.is_complete) {
          if (wasRankingInProgress.current && isBackgroundRankingRef.current) {
            setRankingCompleted(true);
          }
          setPollingEnabled(false);
          setHasCalibrationSetting(data.has_calibration);
        } else {
          wasRankingInProgress.current = true;
        }

        return data.is_complete;
      } catch (error) {
        errorShown.current = true;
        return false; // Keep polling after error
      }
    },
    staleTime: 0,
    enabled: pollingEnabled,
    refetchInterval: pollingEnabled ? interval : false,
  });

  const rankingInProgress =
    !firstPoll && (query.isLoading || query.data === false);

  return (
    <RankingInProgressContext.Provider
      value={{
        rankingInProgress,
        rankingCompleted,
        isBackgroundRanking,
        hasCalibrationSetting,
        enablePolling: () => {
          isBackgroundRankingRef.current = false;
          setIsBackgroundRanking(false);
          setPollingEnabled(true);
        },
        dismissRankingCompleted: () => setRankingCompleted(false),
      }}
    >
      {children}
    </RankingInProgressContext.Provider>
  );
};

export const useRankingInProgress = () => useContext(RankingInProgressContext);
